import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { isV2 } from "../lib/config";
import { keys } from "./queries";

/** One step of a price ladder: `blocks` blocks of `blockMinutes` at `price` fils each; `null` blocks = repeats until the visit ends. */
export interface LadderStep { blockMinutes: number; price: number; blocks: number | null }

export interface PriceRuleView {
  id: string; productId: string; productName: string; branchId: string | null; ageGroupId: string | null; name: string; nameAr: string | null; priority: number;
  taxInclusive: boolean; graceMinutes: number; overtimeBlockMinutes: number; overtimeBlockPrice: number | null; dailyCap: number | null;
  notifyEnabled: boolean; notifyBeforeMinutes: number; active: boolean; openVisits: number; ladder: LadderStep[]; ladderRegular: boolean;
}

export interface RuleDraft {
  productId: string; name: string; nameAr: string; ageGroupId: string | null; thisBranchOnly: boolean; priority: number; taxInclusive: boolean; graceMinutes: number;
  ladder: LadderStep[]; overtimeBlockMinutes?: number; overtimeBlockPrice?: number | null; dailyCap: number | null; notifyEnabled: boolean; notifyBeforeMinutes: number; active: boolean;
}

export interface AdminProduct {
  id: string; sku: string; barcode: string | null; name: string; nameAr: string | null;
  kind: "play_time" | "addon" | "fnb" | "merch" | "combo" | "party_package" | "party_addon" | "school_package" | "credit_topup";
  price: number | null; active: boolean; taxRateId: string | null; consumesStock: boolean; priceOverride: number | null; branchActive: boolean; qtyOnHand: number | null;
}
export interface ComboView {
  id: string; sku: string; name: string; nameAr: string | null; price: number; active: boolean; taxRateId: string | null;
  items: Array<{ productId: string; name: string; nameAr: string | null; price: number | null; qty: number }>; partsWorth: number; saving: number;
}
export interface StockView {
  id: string; sku: string; name: string; nameAr: string | null; uom: string; unitCost: number; active: boolean; qtyOnHand: number | null; parLevel: number | null; reorderQty: number | null;
  supplier: string | null; allowNegative: boolean | null; stocked: boolean;
}
export interface CatalogAdminView {
  products: AdminProduct[]; rules: PriceRuleView[]; combos: ComboView[]; stock: StockView[];
  ageGroups: Array<{ id: string; code: string; name: string; name_ar: string | null; min_months: number; max_months: number | null }>;
  taxRates: Array<{ id: string; code: string; name: string; rate_bp: number }>;
  settings: { defaultBillingMode: "prepaid" | "postpaid" };
}

export const adminKey = ["catalog-admin"] as const;
export const useCatalogAdmin = (enabled = true) => useQuery({ queryKey: adminKey, enabled: enabled && isV2(), queryFn: () => api.get<CatalogAdminView>("/catalog-admin") });

/** Anything that edits prices must refresh the cashier's views too (tickets, catalog, running bills). */
export function useCatalogMutation<I, O = unknown>(fn: (input: I) => Promise<O>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      await Promise.all([adminKey, keys.catalog, keys.sessions, keys.inventory].map((k) => qc.invalidateQueries({ queryKey: k })));
    },
  });
}

export interface SimulateRow { minutes: number; billableMinutes: number; blocks: number; price: number; capped: boolean }

/** Rule form -> the body the API takes (money already in fils). */
export function ruleBody(d: RuleDraft) {
  return {
    productId: d.productId, name: d.name.trim(), nameAr: d.nameAr.trim() || null, ageGroupId: d.ageGroupId, thisBranchOnly: d.thisBranchOnly, priority: d.priority,
    taxInclusive: d.taxInclusive, graceMinutes: d.graceMinutes, ladder: d.ladder, overtimeBlockMinutes: d.overtimeBlockMinutes, overtimeBlockPrice: d.overtimeBlockPrice,
    dailyCap: d.dailyCap, notifyEnabled: d.notifyEnabled, notifyBeforeMinutes: d.notifyBeforeMinutes, active: d.active,
  };
}

/** The defaults of a new rule: the business rule "first hour 8 JOD, every further started hour 4 JOD". */
export function defaultRuleDraft(productId: string): RuleDraft {
  return {
    productId, name: "", nameAr: "", ageGroupId: null, thisBranchOnly: false, priority: 0, taxInclusive: true, graceMinutes: 0,
    ladder: [{ blockMinutes: 60, price: 8000, blocks: 1 }, { blockMinutes: 60, price: 4000, blocks: null }],
    dailyCap: null, notifyEnabled: true, notifyBeforeMinutes: 10, active: true,
  };
}

export function draftOf(r: PriceRuleView): RuleDraft {
  return {
    productId: r.productId, name: r.name, nameAr: r.nameAr ?? "", ageGroupId: r.ageGroupId, thisBranchOnly: r.branchId !== null, priority: r.priority, taxInclusive: r.taxInclusive,
    graceMinutes: r.graceMinutes, ladder: r.ladder.map((s) => ({ ...s })), overtimeBlockMinutes: r.overtimeBlockMinutes, overtimeBlockPrice: r.overtimeBlockPrice,
    dailyCap: r.dailyCap, notifyEnabled: r.notifyEnabled, notifyBeforeMinutes: r.notifyBeforeMinutes, active: r.active,
  };
}
